"use client";

import React, { useCallback, useEffect, useState } from "react";
import Image from "next/image";
import Autoplay from "embla-carousel-autoplay";
import { ChevronRight } from "lucide-react";
import { AnimatePresence, Variants, motion, useAnimation } from 'framer-motion';
import { cn } from "@/lib/utils";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "@/components/ui/carousel";
import { useTranslation } from 'next-i18next';
interface Tip {
  title: string;
  title2: string;
  description: string;
  image: string;
}
interface LoadingCarouselProps {
  tips?: Tip[];
  className?: string;
  autoplayInterval?: number;
  showNavigation?: boolean;
  showIndicators?: boolean;
  showProgress?: boolean;
  aspectRatio?: "video" | "square" | "wide";
  onTipChange?: (index: number) => void;
  backgroundTips?: boolean;
  backgroundGradient?: boolean;
  shuffleTips?: boolean;
  animateText?: boolean;
}
const defaultTips: Tip[] = [{
  title: "text-tip1-title",
  title2: "text-tip1-title2",
  description: "text-tip1-description",
  image: "/assets/images/about/about-1-634x373.jpg"
}, {
  title: "text-tip2-title",
  title2: "text-tip2-title2",
  description: "text-tip2-description",
  image: "/assets/images/about/about-6.png"
}, {
  title: "text-tip3-title",
  title2: "text-tip3-title2",
  description: "text-tip3-description",
  image: "/assets/images/about/about-4-634x373.jpg"
}, {
  title: "text-tip4-title",
  title2: "text-tip4-title2",
  description: "text-tip4-description",
  image: "/assets/images/about/about-5.png"
}, {
  title: "text-tip5-title",
  title2: "text-tip5-title2",
  description: "text-tip5-description",
  image: "/assets/images/about/about-7.png"
}];
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
const carouselVariants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction < 0 ? "100%" : "-100%",
    opacity: 0
  })
};
const textVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.3,
      duration: 0.5
    }
  }
};
const aspectRatioClasses = {
  video: "aspect-video",
  square: "aspect-square",
  wide: "aspect-[2/1]"
};
export function LoadingCarousel({
  onTipChange,
  className,
  tips = defaultTips,
  showProgress = true,
  aspectRatio = "video",
  showNavigation = false,
  showIndicators = true,
  backgroundTips = false,
  autoplayInterval = 4500,
  backgroundGradient = false,
  shuffleTips = false
}: LoadingCarouselProps) {
  const {
    t
  } = useTranslation('common');
  const [progress, setProgress] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const controls = useAnimation();
  const [displayTips] = useState(() => shuffleTips ? shuffleArray(tips) : tips);
  const autoplay = Autoplay({
    delay: autoplayInterval,
    stopOnInteraction: false
  });
  useEffect(() => {
    if (!api) {
      return;
    }
    setCurrent(api.selectedScrollSnap());
    setDirection(api.scrollSnapList().indexOf(api.selectedScrollSnap()) - current);
    const onSelect = () => {
      const newIndex = api.selectedScrollSnap();
      setCurrent(newIndex);
      setDirection(api.scrollSnapList().indexOf(newIndex) - current);
      onTipChange?.(newIndex);
    };
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api, current, onTipChange]);
  useEffect(() => {
    if (!showProgress) return;
    const timer = setInterval(() => {
      setProgress(oldProgress => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = 2;
        return Math.min(oldProgress + diff, 100);
      });
    }, autoplayInterval / 50);
    return () => {
      clearInterval(timer);
    };
  }, [showProgress, autoplayInterval]);
  useEffect(() => {
    if (progress === 100) {
      controls.start({
        scaleX: 0
      }).then(() => {
        setProgress(0);
        setTimeout(() => {
          controls.set({
            scaleX: 1
          });
        }, 0);
      });
    } else {
      controls.start({
        scaleX: progress / 100
      });
    }
  }, [progress, controls]);
  const handleSelect = useCallback((index: number) => {
    api?.scrollTo(index);
  }, [api]);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.8,
    ease: "easeOut"
  }} className={cn("w-full max-w-6xl mx-auto rounded-lg bg-muted dark:bg-neutral-900 shadow-[0px_1px_1px_0px_rgba(0,0,0,0.05),0px_1px_1px_0px_rgba(255,252,240,0.5)_inset,0px_0px_0px_1px_hsla(0,0%,100%,0.1)_inset,0px_0px_1px_0px_rgba(28,27,26,0.5)]", className)} data-sentry-element="unknown" data-sentry-component="LoadingCarousel" data-sentry-source-file="loading-carousel.tsx">
      <div className="w-full overflow-hidden rounded-lg">
        <Carousel setApi={setApi} plugins={[autoplay]} className="w-full relative" opts={{
        loop: true
      }} data-sentry-element="Carousel" data-sentry-source-file="loading-carousel.tsx">
          <CarouselContent data-sentry-element="CarouselContent" data-sentry-source-file="loading-carousel.tsx">
            <AnimatePresence initial={false} custom={direction} data-sentry-element="AnimatePresence" data-sentry-source-file="loading-carousel.tsx">
              {(displayTips || []).map((tip, index) => <CarouselItem key={index}>
                <motion.div variants={carouselVariants} initial="enter" animate="center" exit="exit" custom={direction} transition={{
                duration: 0.8,
                ease: "easeInOut"
              }} className={`relative ${aspectRatioClasses[aspectRatio]} w-full flex flex-col md:flex-row items-center px-4`}>
                    <motion.div variants={textVariants} initial="hidden" animate="visible" className="flex-1 p-4 sm:p-6 md:p-8 flex flex-col justify-center">
                      <h2 className="text-neutral-900 dark:text-neutral-100 text-4xl lg:text-4xl font-bold mb-2">
                      {t(tip.title2)}
                      </h2>
                      <p className="text-neutral-700 dark:text-neutral-300 text-base lg:text-lg">
                      {t(tip.description)}
                      </p>
                    </motion.div>
                    <div className="flex-1 relative mt-4 md:mt-0 md:ml-6">
                      <Image src={tip.image} alt={`${tip.title}`} className="object-cover rounded-lg" priority width={450} height={450} />
                      {backgroundGradient && <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent rounded-lg" />}
                    </div>
                  </motion.div>
                </CarouselItem>)}
            </AnimatePresence>
          </CarouselContent>
          {showNavigation && <div className="hidden md:block">
              <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2" />
              <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2" />
            </div>}
        </Carousel>
        <div className={cn("bg-muted dark:bg-neutral-900 p-4 ", showIndicators && !backgroundTips ? "lg:py-2 lg:px-4 " : "")}>
          <div className={cn("flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0", showIndicators && !backgroundTips ? "sm:flex-col space-y-2 items-start gap-3" : "")}>
            {showIndicators && <div className="flex space-x-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto">
                {(displayTips || []).map((_, index) => <motion.button key={index} className={`h-1 w-8 flex-shrink-0 rounded-full ${index === current ? "bg-muted dark:bg-neutral-900" : "bg-primary"}`} initial={false} animate={{
              backgroundColor: index === current ? "#3D3D3E" : "#E6E6E4"
            }} transition={{
              duration: 0.5
            }} onClick={() => handleSelect(index)} aria-label={`Go to tip ${index + 1}`} />)}
              </div>}
            <div className="flex items-center space-x-2 text-primary whitespace-nowrap">
              {backgroundTips ? <span className="text-sm font-medium">
                  Tip {current + 1}/{displayTips?.length || 0}
                </span> : <div className="flex flex-col">
                  <span className="text-base lg:text-2xl xl:font-semibold tracking-tight font-medium text-neutral-900 dark:text-neutral-100">
                  {t(displayTips[current]?.title)}
                  </span>
                </div>}
              {backgroundTips && <ChevronRight className="w-4 h-4" />}
            </div>
          </div>
          {showProgress && <motion.div initial={{
          scaleX: 0
        }} animate={controls} transition={{
          duration: 0.5,
          ease: "linear"
        }} className="h-1 bg-muted dark:bg-neutral-900 origin-left mt-2" />}
        </div>
      </div>
    </motion.div>;
}
export default LoadingCarousel;